import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatDate } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { authApi } from '@/services/auth.api';
import { Button, Spinner } from '@/components/ui';
import { Avatar } from '@/components/avatar';
import { PersonQrCode } from './person-qr-code';

/**
 * One member's printable church membership card — credit-card sized
 * (ISO/IEC 7810 ID-1, in real `mm`, so it prints at true size on any
 * printer), with the church's own name rather than TABITA's. The QR
 * still encodes the person's id, so the same card doubles as the check-in
 * badge `qr-check-in-page.tsx` scans.
 *
 * The church name comes from `GET /auth/memberships` rather than
 * `useAuth`'s own `memberships` state (only ever populated by a live
 * `login()` call — empty on a direct load or a reload of this page) or
 * `GET /tenancy/current` (needs `tenancy.tenant.read`, which most staff
 * who'd print a card — a group leader, a kids check-in operator — do not
 * hold). `/auth/memberships` needs no permission beyond a valid session.
 */
export function PrintCredentialPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const { tenantId } = useAuth();

  const membershipsQuery = useQuery({
    queryKey: ['auth', 'memberships'],
    queryFn: () => authApi.listMemberships(),
  });
  const churchName =
    membershipsQuery.data?.find((membership) => membership.tenantId === tenantId)?.tenantName ?? '';

  const query = useQuery({
    queryKey: ['people', id],
    queryFn: ({ signal }) => peopleApi.get(id, signal),
  });

  useEffect(() => {
    // Waits on both — printing before the church name arrives would leave
    // its line blank on a card that only gets printed once.
    if (query.isSuccess && membershipsQuery.isSuccess) window.print();
  }, [query.isSuccess, membershipsQuery.isSuccess]);

  return (
    <div className="print-page">
      <div className="print-page-toolbar">
        <Link to={`/people/${id}`} className="back-link">
          {t('common.actions.back')}
        </Link>
        <Button type="button" variant="primary" onClick={() => window.print()}>
          {t('common.actions.print')}
        </Button>
      </div>

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.data && (
        <div className="print-credential">
          <header className="print-credential-header">
            <p className="print-credential-church">{churchName}</p>
            <p className="print-credential-kind">{t('people.print.credentialKind')}</p>
          </header>

          <div className="print-credential-body">
            <Avatar
              photoFileId={query.data.photoFileId}
              displayName={query.data.displayName}
              size={52}
            />
            <div className="print-credential-details">
              <p className="print-credential-name">{query.data.displayName}</p>
              <p className="print-credential-since">
                {t('people.print.memberSince', {
                  values: { date: formatDate(query.data.createdAt, locale) },
                })}
              </p>
            </div>
            <PersonQrCode personId={query.data.id} size={96} className="print-credential-qr" />
          </div>
        </div>
      )}
    </div>
  );
}
